import React, { useMemo, useState, useEffect } from "react";
import { Tracker } from "@/models/tracker";
import { Enrichment } from "@/models/enrichment";
import { useTrackerTagsManager } from "@/features/trackers/hooks/useTrackerTagsManager";
import { useTags } from "@/api/tag";
import { Tooltip, IconButton, Switch, Chip, Checkbox } from "@/components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileImport,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import TagManager from "./TagManager";
import { sourcesData } from "@/data/sources";
import { useApiSchema } from "@/utils/api-schema-provider";
import { getSourceFromPath, getSimpleIconUrl, getTrackerValue } from "@/utils/tracker-helpers";
import { getEnrichmentIcon, getTypeLabel } from "@/features/enrichments/utils";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import type { ViewSize } from "@/components/layout/gallery-layout/types";

// Type union pour supporter trackers et enrichissements
export type ResourceType = "tracker" | "enrichment";

export interface ResourceCardProps {
  tracker?: Tracker;
  enrichment?: Enrichment;
  resourceType?: ResourceType;
  onToggleActive?: (itemId: string, isEnabled: boolean) => void;
  onEdit?: (enrichment: Enrichment) => void;
  onTagsUpdate?: (itemId: string, tags: string[]) => void;
  tagsMap?: Record<string, string[]>;
  /** Whether clicking the card navigates to the detail view (default: true for trackers) */
  clickable?: boolean;
  /** Whether to show selection checkbox */
  selectable?: boolean;
  /** Whether this card is currently selected */
  selected?: boolean;
  /** Called when selection state changes */
  onSelectionChange?: (itemId: string, selected: boolean) => void;
  /** View density size */
  viewSize?: ViewSize;
}

/**
 * Composant pour afficher l'icône d'une source
 */
export const SourceIcon: React.FC<{
  source: string | undefined;
  size?: "sm" | "md" | "lg";
}> = ({ source, size = "sm" }) => {
  const { apiSchema } = useApiSchema();

  // Gérer le cas où source est undefined ou null
  if (!source || typeof source !== "string") {
    return null;
  }

  // Déterminer la taille
  const sizeClass =
    size === "sm" ? "w-4 h-4" : size === "md" ? "w-6 h-6" : "w-8 h-8";

  // Cas spécial pour "import" - utiliser l'icône FontAwesome
  if (source.toLowerCase() === "import") {
    return (
      <div className={`flex items-center justify-center ${sizeClass} text-content-primary`}>
        <FontAwesomeIcon
          icon={faFileImport}
          className="w-full h-full"
        />
      </div>
    );
  }

  // Cas spécial pour "Autre" ou sources inconnues - utiliser une icône générique
  if (source.toLowerCase() === "autre") {
    return (
      <div className={`flex items-center justify-center ${sizeClass} text-content-primary`}>
        <FontAwesomeIcon
          icon={faQuestion}
          className="w-full h-full"
        />
      </div>
    );
  }

  // Vérifier d'abord dans sourcesData pour une icône Material-UI
  const sourceData = sourcesData[source.toLowerCase()];
  if (sourceData?.icon) {
    return (
      <div className={`flex items-center justify-center ${sizeClass}`}>
        {sourceData.icon}
      </div>
    );
  }

  // Obtenir le logo depuis l'API schema
  const sourceFromApi = apiSchema?.sources?.[source];
  const logo = sourceFromApi?.metadata?.logo;
  const logoFallback = sourceFromApi?.metadata?.logoFallback;

  // Déterminer la source initiale
  const initialSrc = logo || logoFallback || getSimpleIconUrl(source);
  const [currentSrc, setCurrentSrc] = useState<
    string | React.ReactNode | undefined
  >(initialSrc);
  const [failedSources, setFailedSources] = useState<Set<string>>(new Set());
  const [showFallbackIcon, setShowFallbackIcon] = useState(false);

  // Mettre à jour currentSrc si logo ou logoFallback change
  useEffect(() => {
    setShowFallbackIcon(false);
    if (logo) {
      setCurrentSrc(logo);
      setFailedSources(new Set());
    } else if (logoFallback) {
      setCurrentSrc(logoFallback);
      setFailedSources(new Set());
    } else {
      setCurrentSrc(getSimpleIconUrl(source));
      setFailedSources(new Set());
    }
  }, [logo, logoFallback, source]);

  // Si on doit afficher l'icône de fallback (toutes les sources ont échoué)
  if (showFallbackIcon) {
    return (
      <div className={`flex items-center justify-center ${sizeClass} text-content-tertiary`}>
        <FontAwesomeIcon
          icon={faQuestion}
          className="w-full h-full"
        />
      </div>
    );
  }

  // Si c'est un composant React, l'afficher directement
  if (React.isValidElement(currentSrc)) {
    return (
      <div className={`flex items-center justify-center ${sizeClass}`}>
        {currentSrc}
      </div>
    );
  }

  // Si c'est une string (URL), utiliser une image avec gestion d'erreur
  if (typeof currentSrc === "string") {
    const handleError = () => {
      // Marquer cette source comme ayant échoué
      const newFailedSources = new Set(failedSources).add(currentSrc);
      setFailedSources(newFailedSources);

      // Si le logo principal a échoué, essayer le fallback
      if (
        currentSrc === logo &&
        typeof logoFallback === "string" &&
        !newFailedSources.has(logoFallback)
      ) {
        setCurrentSrc(logoFallback);
      } else if (
        currentSrc === logoFallback ||
        (currentSrc === logo && !logoFallback)
      ) {
        // Si le fallback a échoué ou qu'il n'y a pas de fallback, utiliser Simple Icons
        const simpleIconUrl = getSimpleIconUrl(source);
        if (!newFailedSources.has(simpleIconUrl)) {
          setCurrentSrc(simpleIconUrl);
        } else {
          // Toutes les sources ont échoué, afficher l'icône de fallback
          setShowFallbackIcon(true);
        }
      } else {
        // Simple Icons a aussi échoué, afficher l'icône de fallback
        setShowFallbackIcon(true);
      }
    };

    return (
      <img
        src={currentSrc}
        alt={`${source} icon`}
        className={`${sizeClass} object-contain dark:invert`}
        onError={handleError}
      />
    );
  }

  // Fallback par défaut: utiliser Simple Icons
  return (
    <img
      src={getSimpleIconUrl(source)}
      alt={`${source} icon`}
      className={`${sizeClass} object-contain dark:invert`}
      onError={() => setShowFallbackIcon(true)}
    />
  );
};

// Tableau vide stable pour éviter les re-renders infinis
const EMPTY_TAGS: string[] = [];

/**
 * ResourceCard - Composant unifié pour afficher un tracker ou un enrichissement
 * sous forme de carte avec sélection, tags, et switch actif/inactif.
 */
const ResourceCard: React.FC<ResourceCardProps> = ({
  tracker,
  enrichment,
  resourceType = "tracker",
  onToggleActive,
  onEdit,
  onTagsUpdate,
  tagsMap = {},
  clickable = true,
  selectable = false,
  selected = false,
  onSelectionChange,
  viewSize = "medium",
}) => {
  // Déterminer l'item actuel (tracker ou enrichment)
  const isEnrichment = resourceType === "enrichment";
  const currentItem = isEnrichment ? enrichment : tracker;
  const itemId = currentItem?.id || "";

  // Panel navigation for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();

  // Utiliser les tags depuis tagsMap au lieu de faire une requête individuelle
  // Utiliser useMemo pour stabiliser la référence et éviter les re-renders infinis
  const tags = useMemo(() => {
    return tagsMap[itemId] || (isEnrichment ? enrichment?.tags : tracker?.tags) || EMPTY_TAGS;
  }, [tagsMap, itemId, isEnrichment, enrichment?.tags, tracker?.tags]);

  const {
    isLoading: isLoadingTags,
    toggleTag,
    updateTags,
  } = useTrackerTagsManager(itemId, tags, onTagsUpdate);

  const { data: allTags } = useTags();

  // Use imported utility
  const trackerValue = !isEnrichment && tracker 
    ? getTrackerValue(tracker) 
    : undefined;
  const sourceFromPath = !isEnrichment && tracker ? getSourceFromPath(tracker) : undefined;

  // Valeurs pour l'affichage
  const displayName = isEnrichment 
    ? enrichment?.name 
    : (trackerValue?.value || tracker?.id);
  const displaySubtitle = isEnrichment 
    ? getTypeLabel(enrichment?.type) 
    : sourceFromPath;
  const isActive = isEnrichment 
    ? enrichment?.status === "active" 
    : (tracker && !tracker.is_import ? tracker.status === "active" : false);
  const isImport = isEnrichment ? false : tracker?.is_import;


  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-interactive]")) return;

    if (selectable && onSelectionChange) {
      onSelectionChange(itemId, !selected);
      return;
    }
    if (isEnrichment && enrichment && clickable) {
      const enrichmentName = enrichment.name || getTypeLabel(enrichment.type) || enrichment.id;
      navigateTo(`/enrichments?id=${enrichment.id}`, { title: enrichmentName });
    } else if (!isEnrichment && tracker && clickable) {
      const trackerName = trackerValue?.value || tracker.id;
      navigateTo(`/trackers?id=${tracker.id}`, { title: trackerName });
    }
  };

  const isCardClickable = clickable || selectable;

  // --- Small: ultra-compact one-liner ---
  if (viewSize === "small") {
    return (
      <div
        className={`
          @container resource-card relative overflow-hidden
          rounded-arc-md bg-surface-elevated
          px-2.5 py-1.5
          shadow-arc-sm transition-all duration-arc-fast hover:shadow-arc-md
          border ${selected ? "border-accent ring-1 ring-accent/30 bg-accent/5" : "border-arc-border hover:border-arc-border-strong"}
          ${isCardClickable ? "cursor-pointer" : ""}
        `}
        onClick={handleCardClick}
      >
        <div className="flex items-center gap-2">
          {selectable && (
            <div
              className="flex-shrink-0"
              data-interactive
              onClick={(e) => { e.stopPropagation(); onSelectionChange?.(itemId, !selected); }}
            >
              <Checkbox checked={selected} onChange={() => {}} size="sm" />
            </div>
          )}
          {isEnrichment ? (
            <div className="flex-shrink-0 flex h-4 w-4 items-center justify-center text-accent">
              <FontAwesomeIcon icon={getEnrichmentIcon(enrichment?.type)} className="text-xs" />
            </div>
          ) : (
            <div className="flex-shrink-0">
              <SourceIcon source={sourceFromPath} size="sm" />
            </div>
          )}
          <h3 className="text-xs font-medium text-content-primary truncate flex-1 min-w-0">
            {displayName}
          </h3>
          {!isImport && onToggleActive && (
            <div className="flex-shrink-0" data-interactive onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={isActive}
                onChange={(checked) => onToggleActive(itemId, checked)}
                size="sm"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Large: spacious card with full details ---
  if (viewSize === "large") {
    return (
      <div
        className={`
          @container resource-card relative overflow-hidden
          rounded-arc-lg bg-surface-elevated
          p-4
          shadow-arc-sm transition-all duration-arc-fast hover:shadow-arc-md
          border ${selected ? "border-accent ring-1 ring-accent/30 bg-accent/5" : "border-arc-border hover:border-arc-border-strong"}
          ${isCardClickable ? "cursor-pointer" : ""}
        `}
        onClick={handleCardClick}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          {selectable && (
            <div
              className="flex-shrink-0 mt-0.5"
              data-interactive
              onClick={(e) => { e.stopPropagation(); onSelectionChange?.(itemId, !selected); }}
            >
              <Checkbox checked={selected} onChange={() => {}} size="sm" />
            </div>
          )}
          {isEnrichment ? (
            <div className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent">
              <FontAwesomeIcon icon={getEnrichmentIcon(enrichment?.type)} className="text-lg" />
            </div>
          ) : (
            <div className="flex-shrink-0 flex h-8 w-8 items-center justify-center">
              <SourceIcon source={sourceFromPath} size="md" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-content-primary truncate leading-snug">
              {displayName}
            </h3>
            {displaySubtitle && (
              <span className="text-sm text-content-tertiary">
                {displaySubtitle}
              </span>
            )}
          </div>
          {!isImport && onToggleActive && (
            <div className="flex-shrink-0 flex items-center" data-interactive onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={isActive}
                onChange={(checked) => onToggleActive(itemId, checked)}
                size="sm"
              />
            </div>
          )}
        </div>

        {/* Description */}
        {isEnrichment && enrichment?.description && (
          <p className="text-sm text-content-secondary line-clamp-2 mt-2 ml-11">
            {enrichment.description}
          </p>
        )}

        {/* Tags */}
        <div className="mt-3 ml-11">
          <div
            className="flex flex-wrap gap-1.5 items-center"
            data-interactive
            onClick={(e) => e.stopPropagation()}
          >
            <TagManager
              tags={tags}
              allTags={allTags}
              isLoadingTags={isLoadingTags}
              onTagsChange={updateTags}
              compact={false}
              showTooltip
            />
          </div>
        </div>
      </div>
    );
  }

  // --- Medium (default): balanced card ---
  return (
    <div
      className={`
        @container resource-card relative overflow-hidden 
        rounded-arc-md bg-surface-elevated 
        p-2.5 @xs:p-3
        shadow-arc-sm transition-all duration-arc-fast hover:shadow-arc-md
        border ${selected ? "border-accent ring-1 ring-accent/30 bg-accent/5" : "border-arc-border hover:border-arc-border-strong"}
        ${isCardClickable ? "cursor-pointer" : ""}
      `}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        {selectable && (
          <div
            className="flex-shrink-0"
            data-interactive
            onClick={(e) => { e.stopPropagation(); onSelectionChange?.(itemId, !selected); }}
          >
            <Checkbox checked={selected} onChange={() => {}} size="sm" />
          </div>
        )}
        {isEnrichment ? (
          <div className="flex-shrink-0 flex h-5 w-5 @xs:h-6 @xs:w-6 items-center justify-center text-accent">
            <FontAwesomeIcon icon={getEnrichmentIcon(enrichment?.type)} className="text-sm @xs:text-base" />
          </div>
        ) : (
          <div className="flex-shrink-0">
            <SourceIcon source={sourceFromPath} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h3 className="text-sm @sm:text-base font-semibold text-content-primary truncate leading-tight">
            {displayName}
          </h3>
          {displaySubtitle && (
            <span className="text-xs text-content-tertiary">
              {displaySubtitle}
            </span>
          )}
        </div>

        {!isImport && onToggleActive && (
          <div 
            className="@xs:hidden flex-shrink-0 flex items-center"
            data-interactive
            onClick={(e) => e.stopPropagation()}
          >
            <Switch
              checked={isActive}
              onChange={(checked) => onToggleActive(itemId, checked)}
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Description */}
      {isEnrichment && enrichment?.description && (
        <p className="hidden @xs:block text-xs text-content-secondary line-clamp-1 mt-1.5 ml-7">
          {enrichment.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 mt-2">
        <div
          className="flex flex-wrap gap-1 items-center flex-1 min-w-0"
          data-interactive
          onClick={(e) => e.stopPropagation()}
        >
          <TagManager
            tags={tags}
            allTags={allTags}
            isLoadingTags={isLoadingTags}
            onTagsChange={updateTags}
            compact
            showTooltip
          />
        </div>

        {!isImport && onToggleActive && (
          <div 
            className="hidden @xs:flex flex-shrink-0 items-center gap-1.5"
            data-interactive
            onClick={(e) => e.stopPropagation()}
          >
            <Switch
              checked={isActive}
              onChange={(checked) => onToggleActive(itemId, checked)}
              size="sm"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceCard;
